import * as RadixDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { Icon } from './Icon.js';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  hideCloseButton?: boolean;
  /** Restores focus to a controlling element when no Radix Trigger is rendered. */
  onCloseAutoFocus?: () => void;
}

/** Accessible modal; Radix owns focus trapping, return, Escape, and aria-modal behavior. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  hideCloseButton,
  onCloseAutoFocus,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-overlay backdrop-blur-[2px]" />
        <RadixDialog.Content
          className="parchment-texture fixed left-1/2 top-1/2 z-50 max-h-[88vh] w-[min(92vw,32rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border-strong border-borderStrong bg-parchment p-5 shadow-4 focus:outline-none tablet:p-7"
          onCloseAutoFocus={(event) => {
            if (!onCloseAutoFocus) return;
            event.preventDefault();
            onCloseAutoFocus();
          }}
        >
          <RadixDialog.Title className="font-display text-xl font-semibold text-textPrimary">
            {title}
          </RadixDialog.Title>
          {description && (
            <RadixDialog.Description className="mt-2 font-editorial text-sm text-textSecondary">
              {description}
            </RadixDialog.Description>
          )}
          <div className="mt-5">{children}</div>
          {!hideCloseButton && (
            <RadixDialog.Close
              aria-label="Close"
              className="absolute right-3 top-3 inline-flex min-h-touch min-w-touch items-center justify-center rounded-md text-textSecondary hover:bg-surfaceAlt"
            >
              <span className="sr-only">Close</span>
              <Icon name="close" />
            </RadixDialog.Close>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
